import { keyframes } from '@mui/material/styles'
import { glyphTurn } from 'src/design-system/icons'

import { motion as motionTokens } from './tokens.generated'

// ----------------------------------------------------------------------
// The closed set of named motions, exposed on the theme as `theme.motion`.
//
// A movement that is not on this list does not ship, and nothing invents its
// own duration. The console's budget is `state`, `enter`, `sheet` and `pulse`;
// `reveal` and `gloss` are tier 1 only and are deliberately not exported here.
// `crossfade` is tier 1 too, and is exported below as a named const rather
// than on the theme, because the entry shell is the one screen in this package
// that is not the console and it is the only thing allowed to reach for it.
//
// Reduced motion is honoured once, in `reducedMotionOverrides` applied by
// CssBaseline, rather than per component:
//   state   keeps its colour change, because losing hover feedback costs
//           clarity and gains nothing
//   enter   collapses to an instant appearance
//   sheet   collapses to an instant appearance
//   pulse   becomes a static tint, so live state stays legible without movement
// ----------------------------------------------------------------------

export type MotionName = 'state' | 'enter' | 'sheet' | 'pulse'

const ms = (value: number) => `${value}ms`

const named = (name: MotionName | 'crossfade') => {
  const token = motionTokens[name]
  const easing =
    motionTokens.easing[token.easing as keyof typeof motionTokens.easing] ??
    token.easing
  return {
    duration: token.duration,
    easing,
    /** Ready to drop into a `transition` shorthand after the property list. */
    css: `${ms(token.duration)} ${easing}`,
    /** Build a full `transition` value for the given properties. */
    transition: (...properties: string[]) =>
      properties
        .map((property) => `${property} ${ms(token.duration)} ${easing}`)
        .join(', '),
  }
}

const motion = {
  easing: motionTokens.easing,
  state: named('state'),
  enter: named('enter'),
  sheet: named('sheet'),
  pulse: named('pulse'),
} as const

/**
 * One full turn. The only rotation in the console, and the only place a
 * transform loops.
 *
 * It is a geometry rather than a value, so there is nothing here for the token
 * source to own but the timing and the direction. The timing is `pulse`, the
 * one looping motion the system allows and the one whose whole subject is
 * *this is happening now*; the direction is the glyph's (`glyphTurn`), and a
 * counterclockwise glyph plays this same keyframe in reverse.
 *
 * The keyframe ends where it started. That is what makes the reduced-motion
 * block below (which cuts every animation to a single 0.01ms iteration) leave
 * the mark upright rather than frozen mid-turn, in either direction, and it is
 * why no component needs a second opinion about the preference.
 */
const turn = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
})

/**
 * The `animation` shorthand a mark wears while its subject is live, turning
 * the way that mark is drawn to turn.
 *
 * Not exported on `theme.motion`: that object is the closed budget of *named*
 * motions and two contract tests assert its keys exactly. This is a use of one
 * of those names, not a sixth name.
 *
 * The easing is `linear` and deliberately not `pulse`'s own `inOut`. An eased
 * loop accelerates and decelerates once per revolution, which reads as a wheel
 * being turned by hand rather than as one that is turning; and because the
 * keyframe wraps, the ease-out of one cycle meets the ease-in of the next and
 * the mark visibly stalls at twelve o'clock. `pulse` owns how long, which is
 * the part that has to agree with the rest of the system; a rotation owns its
 * own rate, the way a progress spinner does.
 *
 * `transform-origin: center` is stated because an svg's default origin is its
 * top-left corner in some engines, which turns a rotation into an orbit.
 *
 * It takes the glyph because the direction is the glyph's: the Rented wheel's
 * blades hook counterclockwise, and turned clockwise it read as rolling
 * backward, while the Refresh arrows point clockwise.
 */
export const liveMarkAnimation = (glyph: string) =>
  ({
    animation: `${turn} ${motionTokens.pulse.duration}ms linear infinite${
      glyphTurn(glyph) === 'counterclockwise' ? ' reverse' : ''
    }`,
    transformOrigin: 'center',
  }) as const

export type Motion = typeof motion

/**
 * The dissolve between two photographs, which is not a console motion.
 *
 * It is deliberately off `theme.motion`, beside `reveal` and `gloss`: the
 * console's budget is four named motions, the tier definition and the contract
 * test both say so, and a 1.2 second fade is not something a screen of rows
 * may reach for. What it belongs to is the entry shell (the sign-in panel is
 * the one place in this product where art leads), so it is imported by the one
 * component that draws art, rather than handed to all forty-three pages.
 *
 * Reduced motion is answered by the caller, not here. The stylesheet already
 * drops opacity from the properties a transition may animate, but a slideshow
 * that cuts instead of fading is still movement, so `FleetPhotos` stops
 * advancing altogether.
 */
export const crossfade = {
  ...named('crossfade'),
  /**
   * What the photograph being replaced does: nothing, until the new one has
   * finished arriving over it, and then it cuts while fully covered.
   *
   * Fading both at once is the obvious version and it is wrong. They are
   * stacked over the brand ink, so two half-transparent photographs composite
   * to about 75% coverage at the midpoint and the panel dims through them.
   * At 200ms that read as a blink; at 1.2 seconds it is a dip to dark in the
   * middle of every transition. So only the arriving photograph animates, on
   * top, and the one underneath holds full opacity until it cannot be seen.
   */
  hold: (...properties: string[]) =>
    properties
      .map(
        (property) =>
          `${property} ${ms(0)} linear ${ms(motionTokens.crossfade.duration)}`,
      )
      .join(', '),
}

declare module '@mui/material/styles' {
  interface Theme {
    motion: Motion
  }
  interface ThemeOptions {
    motion?: Motion
  }
}

/**
 * The slots MUI puts an indeterminate progress animation on.
 *
 * They are exempt from the animation reset below, and that is not a style
 * choice: freezing a spinner leaves an operator with no signal that a command
 * is on its way to a car, which is the one thing this interface must never
 * hide. Each keeps its own designed duration rather than being retimed, so a
 * spinner still reads as a spinner.
 *
 * The rotation lives on the CircularProgress *root*, not on its `svg`; the dash
 * lives on the circle. Getting that wrong leaves half an animation running.
 */
const PROGRESS_SLOTS = [
  'MuiCircularProgress-indeterminate',
  'MuiCircularProgress-circleIndeterminate',
  'MuiLinearProgress-bar1Indeterminate',
  'MuiLinearProgress-bar2Indeterminate',
  'MuiLinearProgress-dashed',
]

const NOT_PROGRESS = PROGRESS_SLOTS.map((slot) => `:not(.${slot})`).join('')

/**
 * One `prefers-reduced-motion` block for the whole surface. Transitions and
 * animations that exist to move something collapse to an instant change, while
 * colour transitions are left alone.
 *
 * This is half of the story: Framer Motion writes inline styles every frame,
 * which no media query can reach, so `MotionConfig reducedMotion="user"` in
 * `components/animate/MotionLazyContainer` carries the same preference there.
 *
 * Note what neither half does: hide content. Opacity must never gate
 * visibility, because a throttled page, a print or a screenshot has to show the
 * real thing.
 */
export const reducedMotionOverrides = {
  '@media (prefers-reduced-motion: reduce)': {
    '*, *::before, *::after': {
      // `enter`, `sheet` and any transform-driven movement collapse to an
      // instant change: the property list below no longer contains transform,
      // height or width, so those simply apply.
      transitionProperty:
        'color, background-color, border-color, fill, stroke, box-shadow, outline-color !important',
      // `state` survives at its own duration. Losing hover and focus feedback
      // costs an operator clarity and gains nothing in vestibular terms.
      transitionDuration: `${motionTokens.state.duration}ms !important`,
      scrollBehavior: 'auto !important',
    },
    // `pulse` runs once at zero length and settles on its resting style, so a
    // live indicator becomes a static tint rather than disappearing. Progress
    // indicators are excluded by the selector rather than retimed.
    [`*${NOT_PROGRESS}, *::before, *::after`]: {
      animationDuration: '0.01ms !important',
      animationIterationCount: '1 !important',
    },
  },
} as const

export default motion
